import { Receipt } from '../../types/expense';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { FileText } from 'lucide-react';
import { ReceiptCard } from '../receipt-card';
import { ReceiptImageViewer } from '../receipts/receipt-image-viewer';
import { useState } from 'react';

interface ReceiptGridProps {
  receipts: Receipt[];
  selectedReceipts: string[];
  onReceiptSelect: (receiptId: string, selected: boolean) => void;
  isLoading?: boolean;
  selectable?: boolean;
  onDelete?: (receiptId: string) => void;
  onUpdate?: (receipt: Receipt) => void;
  showDelete?: boolean;
  'data-tour'?: string;
}

export function ReceiptGrid({
  receipts,
  selectedReceipts,
  onReceiptSelect,
  isLoading = false,
  selectable = true,
  onDelete,
  onUpdate,
  showDelete = false,
  'data-tour': dataTour,
}: ReceiptGridProps) {
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  const handleViewReceipt = (receipt: Receipt) => {
    setSelectedReceipt(receipt);
    setImageViewerOpen(true);
  };

  const handleUpdateReceipt = (receipt: Receipt) => {
    if (onUpdate) {
      onUpdate(receipt);
    }
  };

  const handleCloseViewer = (open: boolean) => {
    setImageViewerOpen(open);
    if (!open) {
      // Reset selected receipt when dialog closes
      setSelectedReceipt(null);
    }
  };
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="p-4">
            <div className="flex gap-3">
              <Skeleton className="w-20 h-28 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-3 w-2/3" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (receipts.length === 0) {
    return (
      <Card className="p-12 flex flex-col items-center justify-center text-center">
        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No receipts found</h3>
        <p className="text-sm text-muted-foreground">
          Upload receipts to get started
        </p>
      </Card>
    );
  }

  return (
    <>
      <div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        data-tour={dataTour}
      >
        {receipts.map((receipt) => {
          const isSelected = selectedReceipts.includes(receipt.id);
          return (
            <ReceiptCard
              key={receipt.id}
              receipt={receipt}
              isSelected={isSelected}
              onSelect={(id) => selectable && onReceiptSelect(id, !isSelected)}
              onDelete={onDelete}
              onViewReceipt={handleViewReceipt}
              showDelete={showDelete}
            />
          );
        })}
      </div>

      {selectedReceipt && (
        <ReceiptImageViewer
          open={imageViewerOpen}
          onOpenChange={handleCloseViewer}
          imageData={selectedReceipt.imageData}
          receiptName={selectedReceipt.supplier || 'Receipt'}
          receipt={selectedReceipt}
          onUpdate={handleUpdateReceipt}
        />
      )}
    </>
  );
}
